/** D5 consumer contract. No arbitrary URL, bucket or filesystem locator is accepted. */
export type PdfRequest =
  | { kind: "legacy" }
  | { kind: "invalid" }
  | { kind: "discover"; guideId: string }
  | { kind: "controlled"; guideId: string; version: string; page: number };

export function parsePdfRequest(guideId: string, search: string, requireLocal = false): PdfRequest {
  const params = new URLSearchParams(search);
  if (!params.has("pdfVersion") && !params.has("page")) {
    if (!requireLocal) return { kind: "legacy" };
    return /^[1-9][0-9]{0,18}$/.test(guideId) ? { kind: "discover", guideId } : { kind: "invalid" };
  }
  const version = params.get("pdfVersion") ?? "";
  const page = params.get("page") ?? "1";
  if (
    !/^[1-9][0-9]{0,18}$/.test(guideId) ||
    !/^[a-f0-9]{64}$/.test(version) ||
    !/^[1-9][0-9]{0,4}$/.test(page) ||
    Number(page) > 10000 ||
    params.getAll("pdfVersion").length !== 1 ||
    params.getAll("page").length > 1
  ) {
    return { kind: "invalid" };
  }
  return { kind: "controlled", guideId, version, page: Number(page) };
}

export type ControlledPdfRequest = Extract<PdfRequest, { kind: "controlled" }>;
export type PdfFailure = "unavailable" | "versionChanged" | "tooLarge" | "loadFailed";
export class ControlledPdfError extends Error {
  readonly reason: PdfFailure;
  constructor(reason: PdfFailure) {
    super(reason);
    this.reason = reason;
  }
}
export const MAX_PDF_BYTES = 32 * 1024 * 1024;

export function controlledPdfUrl(request: ControlledPdfRequest): string {
  return `/guide-files/v1/guides/${request.guideId}/pdf?sha256=${request.version}`;
}

/** Fully validate bytes before pdf.js can render them. Time/size bounded, no redirect fallback. */
export async function loadControlledPdf(
  request: ControlledPdfRequest,
  signal: AbortSignal,
): Promise<Uint8Array> {
  const boundedSignal = AbortSignal.any([signal, AbortSignal.timeout(30000)]);
  boundedSignal.throwIfAborted();
  const response = await fetch(controlledPdfUrl(request), {
    signal: boundedSignal,
    redirect: "error",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/pdf" },
  });
  let failure: PdfFailure | undefined;
  if (response.status === 409 || response.status === 412) failure = "versionChanged";
  else if ([403, 404, 410].includes(response.status)) failure = "unavailable";
  else if (response.status === 413) failure = "tooLarge";
  else if (
    response.status !== 200 ||
    response.headers.get("content-type")?.split(";")[0].trim() !== "application/pdf" ||
    !response.body
  )
    failure = "loadFailed";
  if (failure || !response.body) {
    await response.body?.cancel().catch(() => {});
    throw new ControlledPdfError(failure ?? "loadFailed");
  }
  const bytes = await readBoundedBody(response, boundedSignal, MAX_PDF_BYTES, "tooLarge");
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  boundedSignal.throwIfAborted();
  const version = Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join(
    "",
  );
  if (version !== request.version) throw new ControlledPdfError("versionChanged");
  return bytes;
}

async function readBoundedBody(
  response: Response,
  boundedSignal: AbortSignal,
  maxBytes: number,
  tooLargeReason: PdfFailure,
): Promise<Uint8Array<ArrayBuffer>> {
  if (!response.body) throw new ControlledPdfError("loadFailed");
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    if (Number(response.headers.get("content-length")) > maxBytes)
      throw new ControlledPdfError(tooLargeReason);
    for (;;) {
      boundedSignal.throwIfAborted();
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > maxBytes) throw new ControlledPdfError(tooLargeReason);
      chunks.push(value);
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
  boundedSignal.throwIfAborted();
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
}

/** Discover only a version the trusted local handler has checked; never consume a locator URL. */
export async function discoverControlledPdf(
  guideId: string,
  signal: AbortSignal,
): Promise<ControlledPdfRequest> {
  if (!/^[1-9][0-9]{0,18}$/.test(guideId)) throw new ControlledPdfError("loadFailed");
  const boundedSignal = AbortSignal.any([signal, AbortSignal.timeout(8000)]);
  boundedSignal.throwIfAborted();
  const response = await fetch(`/guide-files/v1/guides/${guideId}/metadata`, {
    signal: boundedSignal,
    redirect: "error",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  let failure: PdfFailure | undefined;
  if (response.status === 412) failure = "versionChanged";
  else if ([403, 404, 410].includes(response.status)) failure = "unavailable";
  else if (response.status === 413) failure = "tooLarge";
  else if (
    response.status !== 200 ||
    response.headers.get("content-type")?.split(";")[0].trim() !== "application/json" ||
    !response.body
  )
    failure = "loadFailed";
  if (failure) {
    await response.body?.cancel().catch(() => {});
    throw new ControlledPdfError(failure);
  }
  const bytes = await readBoundedBody(response, boundedSignal, 2048, "loadFailed");
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch {
    throw new ControlledPdfError("loadFailed");
  }
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new ControlledPdfError("loadFailed");
  const metadata = value as Record<string, unknown>;
  if (
    Object.keys(metadata).sort().join(",") !== "bytes,guideId,sha256,status,version" ||
    metadata.version !== 1 ||
    metadata.guideId !== guideId ||
    metadata.status !== "available" ||
    typeof metadata.sha256 !== "string" ||
    !/^[a-f0-9]{64}$/.test(metadata.sha256) ||
    typeof metadata.bytes !== "number" ||
    !Number.isSafeInteger(metadata.bytes) ||
    metadata.bytes < 1 ||
    metadata.bytes > MAX_PDF_BYTES
  )
    throw new ControlledPdfError("loadFailed");
  boundedSignal.throwIfAborted();
  return { kind: "controlled", guideId, version: metadata.sha256, page: 1 };
}
